import { MESSAGE_LIMITS } from '@barso/shared';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  messagingApi,
  type MessageItem,
  type MessageThreadDetail,
  type OrgBroadcastView,
} from '../api/endpoints';
import { messageFor } from '../api/messages';
import { MailboxShell, NewMessageLink } from '../components/Mailbox';
import { MessageList, ReplyForm } from '../components/MessageThreadView';
import { Alert, Button, Card, PageHeader, TextField } from '../components/ui';
import { notifyUnreadChanged } from '../hooks/useUnreadMessages';

/**
 * Das Postfach einer Person (Plan 16, 4.13): eine Liste für alle Rollen, auf dem Desktop mit dem Verlauf daneben.
 * Wer Team-Recht hat, sieht auch die Gespräche der eigenen Basare.
 */
export function InboxPage() {
  return (
    <MailboxShell
      header={
        <div className="row row--between">
          <h1>Nachrichten</h1>
          <NewMessageLink to="/nachrichten/neu" />
        </div>
      }
    />
  );
}

/** Rundschreiben einer Organisation (nur Lesen, keine Antwort). Beim Öffnen gilt es als gelesen. */
export function OrgMessagePage() {
  const { broadcastId = '' } = useParams();
  const [view, setView] = useState<OrgBroadcastView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const guard = { cancelled: false };
    messagingApi.myOrgBroadcast(broadcastId).then(
      (loaded) => {
        if (guard.cancelled) return;
        setView(loaded);
        if (loaded.unread) {
          messagingApi.markOrgBroadcastRead(broadcastId).then(notifyUnreadChanged, () => undefined);
        }
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [broadcastId]);

  const back = { to: '/nachrichten', label: 'Nachrichten' };
  if (view === null) {
    return error === null ? (
      <p role="status">Einen Moment bitte …</p>
    ) : (
      <div className="stack">
        <PageHeader back={back} title="Nachricht" />
        <Alert kind="error">{error}</Alert>
      </div>
    );
  }
  const item: MessageItem = {
    id: view.id,
    side: 'team',
    mine: false,
    senderName: view.senderName,
    text: view.text,
    redacted: view.redacted,
    createdAt: view.createdAt,
  };
  return (
    <div className="stack stack--lg">
      <PageHeader
        back={back}
        title={view.subject}
        meta={
          <>
            {view.organizationName} · Rundschreiben der Organisation · {view.audienceLabel}
          </>
        }
      />
      <MessageList messages={[item]} />
      <Alert kind="info">Auf Rundschreiben kann man nicht antworten.</Alert>
    </div>
  );
}

/** Neues Gespräch mit dem Team eines Basars (Betreff und Text). Danach öffnet sich der neue Faden. */
export function AskTeamForm({
  eventId,
  initialSubject = '',
}: {
  eventId: string;
  initialSubject?: string;
}) {
  const navigate = useNavigate();
  const [subject, setSubject] = useState(initialSubject);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    messagingApi.startThread({ eventId, subject, text }).then(
      (thread) => {
        notifyUnreadChanged();
        void navigate(`/nachrichten/${thread.id}`);
      },
      (caught: unknown) => {
        setError(messageFor(caught));
        setBusy(false);
      },
    );
  };

  return (
    <Card>
      <form className="stack" onSubmit={submit}>
        {error !== null && <Alert kind="error">{error}</Alert>}
        <TextField
          label="Betreff"
          required
          maxLength={MESSAGE_LIMITS.subjectMax}
          value={subject}
          onChange={(changeEvent) => {
            setSubject(changeEvent.target.value);
          }}
        />
        <div className="field">
          <label htmlFor="nachricht-text">Nachricht</label>
          <textarea
            id="nachricht-text"
            rows={6}
            required
            maxLength={MESSAGE_LIMITS.textMax}
            value={text}
            onChange={(changeEvent) => {
              setText(changeEvent.target.value);
            }}
          />
          <span className="field__hint">
            {text.length} von {MESSAGE_LIMITS.textMax} Zeichen. Nur Text, keine Anhänge. Bitte keine
            Passwörter oder Zahlungsdaten senden.
          </span>
        </div>
        <div>
          <Button
            icon="mail"
            type="submit"
            busy={busy}
            disabled={subject.trim() === '' || text.trim() === ''}
          >
            Nachricht senden
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Verlauf eines Gesprächs oder Rundschreibens. Beim Öffnen gilt der Faden als gelesen. */
export function ThreadPage() {
  const { threadId = '' } = useParams();
  const [thread, setThread] = useState<MessageThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const guard = { cancelled: false };
    messagingApi.myThread(threadId).then(
      (loaded) => {
        if (guard.cancelled) return;
        setThread(loaded);
        if (loaded.unread) {
          messagingApi.markMineRead(threadId).then(notifyUnreadChanged, () => undefined);
        }
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [threadId]);

  if (thread === null) {
    return error === null ? (
      <p role="status">Einen Moment bitte …</p>
    ) : (
      <div className="stack">
        <PageHeader back={{ to: '/nachrichten', label: 'Nachrichten' }} title="Nachricht" />
        <Alert kind="error">{error}</Alert>
      </div>
    );
  }

  const loadOlder = (): void => {
    const first = thread.messages[0];
    if (first === undefined) return;
    setBusy(true);
    messagingApi.myThread(threadId, first.id).then(
      (older) => {
        setThread((current) =>
          current === null
            ? current
            : {
                ...current,
                messages: [...older.messages, ...current.messages],
                hasMore: older.hasMore,
              },
        );
        setBusy(false);
      },
      (caught: unknown) => {
        setError(messageFor(caught));
        setBusy(false);
      },
    );
  };

  const reply = async (text: string): Promise<void> => {
    const sent: MessageItem = await messagingApi.replyMine(threadId, text);
    setThread((current) =>
      current === null
        ? current
        : { ...current, messages: [...current.messages, sent], unread: false },
    );
    notifyUnreadChanged();
  };

  const askTeam = `/nachrichten/neu?basar=${encodeURIComponent(thread.eventId)}&betreff=${encodeURIComponent(
    `Re: ${thread.subject}`.slice(0, MESSAGE_LIMITS.subjectMax),
  )}`;

  return (
    <div className="stack stack--lg">
      <PageHeader
        back={{ to: '/nachrichten', label: 'Nachrichten' }}
        title={thread.subject}
        meta={
          <>
            {thread.eventTitle} · {thread.organizationName} ·{' '}
            {thread.kind === 'broadcast' ? 'Rundschreiben' : 'Gespräch'}
          </>
        }
      />
      {error !== null && <Alert kind="error">{error}</Alert>}
      {thread.hasMore && (
        <div>
          <Button icon="chevron-down" variant="secondary" busy={busy} onClick={loadOlder}>
            Ältere Nachrichten laden
          </Button>
        </div>
      )}
      <MessageList messages={thread.messages} />
      {thread.kind === 'broadcast' ? (
        <Alert kind="info">
          Auf Rundschreiben kann man nicht antworten. Haben Sie eine Frage dazu?{' '}
          <Link to={askTeam}>Frage an das Team</Link>
        </Alert>
      ) : thread.canReply ? (
        <Card>
          <ReplyForm maxLength={MESSAGE_LIMITS.textMax} onSend={reply} />
        </Card>
      ) : (
        <Alert kind="info">Auf dieses Gespräch können Sie nicht mehr antworten.</Alert>
      )}
    </div>
  );
}
